<template>
    <Headify :data="headMeta" />

    <div class="min-h-screen bg-gray-100">
        <NavMain :key="route.name" />

        <div class="tpl-body active-edit:pr-96 lg:pl-64">
            <main class="template-content-wrapper focus:outline-hidden">
                <div class="z-[1] w-full bg-white">
                    <TemplatePDFComponent
                        v-if="templateModel.type === 'PDF'"
                        :template-edit-model="templateEditModel"
                        :active-components="activeComponents"
                    />
                    <TemplateEmailComponent
                        v-if="templateModel.type === 'EMAIL'"
                        :template-edit-model="templateEditModel"
                        :active-components="activeComponents"
                    />
                    <TemplateSMSComponent
                        v-if="templateModel.type === 'SMS'"
                        :template-version-model="templateVersionModel"
                        :active-components="activeComponents"
                        :state-repository="stateRepository"
                    />
                    <TemplateINBOXComponent
                        v-if="templateModel.type === 'INBOX'"
                        :template-version-model="templateVersionModel"
                        :template-model="templateModel"
                        :template-edit-model="templateEditModel"
                        :active-components="activeComponents"
                    />
                </div>
            </main>
        </div>
        <!-- Responsive sidebar toggle button -->
        <div
            :class="[
                activeComponents.sidebar.active
                    ? 'translate-x-full'
                    : 'active-edit:translate-x-full translate-x-0',
                'active-edit:static active-edit:hidden fixed top-28 right-0 z-[1999] h-10 w-10 transform transition-transform delay-300 duration-300',
            ]"
        >
            <button
                type="button"
                class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-l-md bg-gray-100 p-0 shadow-lg focus:ring-0 focus:outline-hidden"
                @click="activeComponents.sidebar.active = true"
            >
                <span class="sr-only">Show sidebar</span>
                <MdiArrowLeft
                    class="h-6 w-6 text-gray-700"
                    aria-hidden="true"
                />
            </button>
        </div>
        <!-- Responsive sidebar -->
        <aside
            :class="[
                activeComponents.sidebar.active
                    ? 'translate-x-0'
                    : 'active-edit:translate-x-0 translate-x-full',
                'fixed top-12 right-0 bottom-0 z-2000 flex max-h-[100vh-54px] min-h-[100vh-54px] w-full min-w-full transform flex-col bg-white shadow-lg transition-transform duration-300 sm:w-96 sm:min-w-96 lg:top-0 lg:h-screen lg:max-h-screen lg:min-h-screen',
            ]"
        >
            <!-- Sidebar control buttons -->
            <div
                class="flex-none border-b border-gray-200 bg-gray-100 px-2"
                @click.stop
            >
                <!-- Responsive sidebar close button -->
                <div class="active-edit:static active-edit:hidden absolute top-2 right-1 h-8 w-8">
                    <button
                        type="button"
                        class="flex h-8 w-8 cursor-pointer items-center justify-center bg-transparent p-0 text-gray-500 hover:text-gray-700 focus:ring-0 focus:outline-hidden"
                        @click="activeComponents.sidebar.active = false"
                    >
                        <span class="sr-only">Close sidebar</span>
                        <MdiClose
                            class="h-6 w-6"
                            aria-hidden="true"
                        />
                    </button>
                </div>
                <!-- Responsive sidebar section control buttons -->
                <nav
                    class="-mb-px flex space-x-8"
                    aria-label="Tabs"
                >
                    <button
                        v-for="(tab, i) in activeComponents.sidebar.tabs"
                        :key="tab.name"
                        :class="[
                            i === activeComponents.sidebar.id
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                            'cursor-pointer border-b-2 px-2 py-4 text-sm font-medium whitespace-nowrap',
                        ]"
                        @click="setCurrentTab(i)"
                    >
                        {{ tab.title }}
                    </button>
                </nav>
            </div>

            <!-- Section wrapper with fade transition -->
            <div class="relative flex grow overflow-x-hidden overflow-y-auto">
                <Transition
                    enter-active-class="transition-transform duration-200 ease-in-out"
                    leave-active-class="transition-transform duration-200 ease-in-out"
                    enter-from-class="-translate-x-full"
                    enter-to-class="translate-x-0"
                    leave-from-class="translate-x-0"
                    leave-to-class="translate-x-full"
                    mode="out-in"
                >
                    <div
                        v-if="activeComponents.sidebar.id === 0"
                        key="section-0"
                        class="grow overflow-y-auto px-4 pt-2"
                    >
                        <TemplatePreviewWidget
                            :key="getRndKey(17)"
                            :template-version-model="templateVersionModel"
                            :template-model="templateModel"
                            :template-edit-model="templateEditModel"
                        />
                        <PlaceholderWidget
                            :template-version-model="templateVersionModel"
                            :template-model="templateModel"
                            :template-edit-model="templateEditModel"
                            :active-components="activeComponents"
                        />
                        <BlockButtonsWidget
                            :key="getRndKey(17)"
                            :template-edit-model="templateEditModel"
                            :row-id="activeComponents.content.rowId"
                            :state-repository="stateRepository"
                        />
                        <BlockDeleteWidget
                            :key="getRndKey(17)"
                            :template-model="templateEditModel"
                            :row-id="activeComponents.content.rowId"
                            :state-repository="stateRepository"
                        />
                    </div>
                    <div
                        v-else-if="activeComponents.sidebar.id === 1"
                        key="section-1"
                        class="grow overflow-y-auto px-4 pt-2"
                    >
                        <PaddingWidget
                            :key="getRndKey(17)"
                            :members="activeComponents.content.style"
                            :state-repository="stateRepository"
                        />
                        <ColorsWidget
                            :key="getRndKey(17)"
                            :members="activeComponents.content.style"
                            :state-repository="stateRepository"
                        />
                        <TextAlignWidget
                            :key="getRndKey(17)"
                            :members="activeComponents.content.style"
                            :state-repository="stateRepository"
                        />
                        <SizeWidget
                            :key="getRndKey(17)"
                            :members="activeComponents.content.style"
                            :state-repository="stateRepository"
                        />
                    </div>
                    <div
                        v-else-if="activeComponents.sidebar.id === 2"
                        key="section-2"
                        class="grow overflow-y-auto px-4 pt-2"
                    >
                        <div class="www pt-6 pb-2 select-none">
                            <BasicsWidget
                                key="basics-widget"
                                :template-version-edit="templateVersionModel"
                                :template-version-id="templateVersionId"
                                :template-validation="templateValidation"
                            />
                            <TemplateApprovement
                                key="template-approvement"
                                :template-version-model="templateVersionModel"
                            />
                            <AccessiblePDF
                                key="accessible-pdf"
                                :template-version-model="templateVersionModel"
                            />
                            <MetaWidget
                                key="meta-widget"
                                :template-edit-model="templateEditModel"
                            />
                            <BlockShowWidget
                                key="block-show-widget"
                                :active-components="activeComponents"
                                :state-repository="stateRepository"
                            />
                            <WideSMSEditorWidget
                                v-if="stateRepository.states.wideEditor"
                                key="wide-sms-editor"
                                :state-repository="stateRepository"
                            />
                        </div>
                    </div>
                </Transition>
            </div>
            <!-- Saving bar -->
            <div
                class="www item-end flex h-16 flex-none items-center justify-end border-t border-gray-100 px-4 py-2 text-right"
            >
                <div class="flex flex-row justify-end gap-2">
                    <ActionButton
                        :value="t('buttonBack')"
                        state="neutral"
                        :icon="MdiChevronLeft"
                        @click="
                            router.push({
                                name: 'template-versions',
                                params: {
                                    templateid: templateId,
                                },
                            })
                        "
                    />
                    <div class="inline-flex rounded-md shadow-sm">
                        <ActionButton
                            :value="t('buttonSave')"
                            state="primary"
                            :icon="MdiContentSave"
                            class-override="rounded-r-none"
                            @click="isValidForSave(false)"
                        />
                        <Menu
                            as="div"
                            class="relative block"
                        >
                            <transition
                                enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95"
                            >
                                <MenuItems
                                    class="absolute right-0 bottom-10 z-10 mt-2 -mr-1 w-56 origin-bottom-right rounded-md bg-blue-500 shadow-lg focus:outline-hidden"
                                >
                                    <div class="py-1">
                                        <MenuItem>
                                            <ActionButton
                                                :value="t('buttonSaveAndClose')"
                                                state="primary"
                                                :icon="MdiContentSaveMove"
                                                class-override="block w-full"
                                                @click="isValidForSave(true)"
                                            />
                                        </MenuItem>
                                    </div>
                                </MenuItems>
                            </transition>
                            <MenuButton
                                class="relative inline-flex items-center rounded-r-md bg-blue-500 px-2 py-2 text-gray-100 hover:bg-blue-600 focus:z-10"
                            >
                                <span class="sr-only">Open options</span>
                                <MdiChevronUp
                                    class="h-5 w-5"
                                    aria-hidden="true"
                                />
                            </MenuButton>
                        </Menu>
                    </div>
                </div>
            </div>
        </aside>
    </div>

    <Spinner
        v-if="saving.active"
        type="dots"
        :color="`text-blue-600`"
        :color2="`text-blue-400`"
        :text-color="`text-gray-200`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="t('loading')"
        :inline="false"
        :pulse="true"
    />
</template>

<script setup>
import { reactive, watch, provide, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Headify from '../components/Headify.vue';
import Spinner from '../ui/Spinner.vue';
import NavMain from '../ui/NavMain.vue';
import TemplatePDFComponent from '../components/TemplatePDF.vue';
import TemplateINBOXComponent from '../components/TemplateINBOX.vue';
import TemplateEmailComponent from '../components/TemplateEmail.vue';
import TemplateSMSComponent from '../components/TemplateSMS.vue';
import PaddingWidget from '../editor-widgets/PaddingWidget.vue';
import TextAlignWidget from '../editor-widgets/TextAlignWidget.vue';
import ColorsWidget from '../editor-widgets/ColorsWidget.vue';
import SizeWidget from '../editor-widgets/SizeWidget.vue';
import BlockButtonsWidget from '../editor-widgets/BlockButtonsWidget.vue';
import BlockShowWidget from '../editor-widgets/BlockShowWidget.vue';
import WideSMSEditorWidget from '../editor-widgets/WideSMSEditorWidget.vue';
import BlockDeleteWidget from '../editor-widgets/BlockDeleteWidget.vue';
import TemplatePreviewWidget from '../editor-widgets/TemplatePreviewWidget.vue';
import PlaceholderWidget from '../editor-widgets/PlaceholderWidget.vue';
import MetaWidget from '../editor-widgets/MetaWidget.vue';
import BasicsWidget from '../editor-widgets/BasicsWidget.vue';
import TemplateApprovement from '../editor-widgets/TemplateApprovementWidget.vue';
import AccessiblePDF from '../editor-widgets/AccessiblePDF.vue';
import ActionButton from '../ui/ActionButton.vue';
import { notify } from 'notiwind';
import { ark, cloneData, getRndKey, checkVersionFormat, keeper } from '../composables/utils';
import navs from '../composables/navs';
import {
    getTemplateVersion,
    saveTemplateVersion,
    getTemplate,
    parseUiConfig,
    recoverFromSource,
    isEmptyModel,
} from '../composables/useTemplates';
import {
    MdiContentSave,
    MdiContentSaveMove,
    MdiChevronLeft,
    MdiChevronUp,
    MdiClose,
    MdiArrowLeft,
} from 'materialdesignicons-vue3/icons/';
import { t } from '../composables/i18n';
import { createRows, addRows, defaultTemplateModel } from '../composables/useTemplateModel';
import { renderTemplate } from '../composables/useTemplateRenderer';
import {
    listPlaceholderRefs,
    savePlaceholderRef,
    removePlaceholderRef,
} from '../composables/usePlaceholderRefs';
import { listPlaceholders } from '../composables/usePlaceholders';
import { harvestTemplate } from '../composables/useFreemarker';
import { preparePlaceholdersDatasets } from '../composables/usePlaceholderSync';
import { cleanHtml, fixDOM, fixBrokenMarkup } from '../composables/useTemplateExtras';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { batch as pdfBatch } from '../template-init/bootstrap/pdf/template-boot-body.js';
import { head as pdfHead } from '../template-init/bootstrap/pdf/template-boot-head.js';
import { meta as pdfMeta } from '../template-init/bootstrap/pdf/template-boot-meta.js';
import { batch as emailBatch } from '../template-init/bootstrap/email/template-boot-body.js';
import { head as emailHead } from '../template-init/bootstrap/email/template-boot-head.js';
import { meta as emailMeta } from '../template-init/bootstrap/email/template-boot-meta.js';
import { batch as smsBatch } from '../template-init/bootstrap/sms/template-boot-body.js';
import { head as smsHead } from '../template-init/bootstrap/sms/template-boot-head.js';
import { meta as smsMeta } from '../template-init/bootstrap/sms/template-boot-meta.js';
import { batch as inboxBatch } from '../template-init/bootstrap/inbox/template-boot-body.js';
import { head as inboxHead } from '../template-init/bootstrap/inbox/template-boot-head.js';
import { meta as inboxMeta } from '../template-init/bootstrap/inbox/template-boot-meta.js';

import '../template-init/editor-styles/pdf/client-styles.scss';
import '../template-init/editor-styles/email/client-styles.scss';
import '../template-init/editor-styles/sms/client-styles.scss';
import '../template-init/editor-styles/inbox/client-styles.scss';

const route = useRoute();
const router = useRouter();
const templateId = route.params.templateid || '0';
const templateVersionId = route.params.templateversionid || '0';
const templateModel = reactive((await getTemplate(templateId)) || {});
const config = inject('config');

if (isEmptyModel(templateModel)) {
    document.location.href = '/404';
}

const templateVersionModel = reactive((await getTemplateVersion(templateVersionId)) || {});

if (templateVersionId !== '0' && isEmptyModel(templateVersionModel)) {
    document.location.href = '/404';
}

let _meta, _head, _body, _uiConfig;

if (templateModel.type.toLowerCase() === 'inbox') {
    _meta = inboxMeta;
    _head = inboxHead;
    _body = inboxBatch;
} else if (templateModel.type.toLowerCase() === 'email') {
    _meta = emailMeta;
    _head = emailHead;
    _body = emailBatch;
} else if (templateModel.type.toLowerCase() === 'sms') {
    _meta = smsMeta;
    _head = smsHead;
    _body = smsBatch;
} else {
    _meta = pdfMeta;
    _head = pdfHead;
    _body = pdfBatch;
}

if (templateVersionId === '0') {
    templateVersionModel.templateId = templateId.toString();
    templateVersionModel.langCode = config.env.defualtLang.toUpperCase();
    templateVersionModel.version = '1.0';
    templateVersionModel.state = 'DRAFT';
    templateVersionModel.uiConfig = '{}';
} else {
    _uiConfig = parseUiConfig(templateVersionModel.uiConfig) || {};

    if (!_uiConfig.template) {
        const autoDetectLayout = recoverFromSource(templateVersionModel, cloneData(_uiConfig), {
            meta: _meta,
            head: _head,
            body: _body,
        });

        _meta = autoDetectLayout.template.meta;
        _head = autoDetectLayout.template.head;
        _body = autoDetectLayout.template.body;
        /* PARTS CHECK
        console.log('RECOVERY POST _meta', _meta);
        console.log('RECOVERY POST _head', _head);
        console.log('RECOVERY POST _body', _body);
        */
    }

    _meta = _uiConfig.template && _uiConfig.template.meta ? _uiConfig.template.meta : _meta;
    _head = _uiConfig.template && _uiConfig.template.head ? _uiConfig.template.head : _head;
    _body = _uiConfig.template && _uiConfig.template.body ? _uiConfig.template.body : _body;
}

let templateEditModel = addRows(reactive(defaultTemplateModel(_head)), createRows(_body));
templateEditModel.template.meta = _meta;

// console.log('templateVersionModel', templateVersionModel.uiConfig);
// console.log('templateVersionModel', templateVersionModel.source);
// console.log('templateVersionModel', templateVersionModel.sourceInline);

const templateValidation = reactive({
    version: checkVersionFormat(ark(templateVersionModel, 'version', '')),
    subject: ark(templateVersionModel, 'subject').length > 0,
    changeLog: ark(templateVersionModel, 'changeLog').length > 0,
    source: ark(templateVersionModel, 'source').length > 0,
});
const allPlaceholders = (await listPlaceholders({
    isValid: true,
})) || {
    items: [],
    totalCount: 0,
};

const activeComponents = reactive({
    row: {
        id: '',
    },
    col: {
        id: '',
    },
    content: {
        rowId: '',
        colId: '',
        cssClass: '',
        id: '',
        html: '',
        tag: '',
        inlineStyle: [],
        style: {},
    },
    sidebar: {
        id: 0,
        active: false,
        tabs: [
            {
                name: 'tools',
                title: t('TemplateTools'),
                disabled: true,
            },
            {
                name: 'format',
                title: t('TemplateFormat'),
            },
            {
                name: 'basics',
                title: t('TemplateBasics'),
            },
        ],
    },
    showBlocks: true,
    editor: {
        active: false,
        type: templateModel.type,
        instance: null,
    },
    methods: {},
});

activeComponents.methods['editorIsActive'] = () => {
    return activeComponents.content.id != '' && activeComponents.editor.active === true;
};

const stateRepository = reactive({});
const updateStateRepository = () => {
    stateRepository.editorActive = activeComponents.methods.editorIsActive();
    stateRepository.states = {
        //placeholder: false,
        color:
            ['PDF', 'EMAIL'].includes(activeComponents.editor.type) && stateRepository.editorActive,
        blockDelete:
            ['PDF', 'EMAIL'].includes(activeComponents.editor.type) && stateRepository.editorActive,
        blockButtons:
            ['PDF', 'EMAIL'].includes(activeComponents.editor.type) && stateRepository.editorActive,
        blockShow: ['PDF', 'EMAIL'].includes(activeComponents.editor.type),
        textAlign:
            ['PDF', 'EMAIL'].includes(activeComponents.editor.type) && stateRepository.editorActive,
        padding:
            ['PDF', 'EMAIL'].includes(activeComponents.editor.type) && stateRepository.editorActive,
        size:
            ['PDF', 'EMAIL'].includes(activeComponents.editor.type) && stateRepository.editorActive,
        wideEditor: ['SMS'].includes(activeComponents.editor.type),
        wideEditorActive: keeper('wideEditor') ?? false,
    };
};
updateStateRepository();

const saving = reactive({
    active: false,
    text: '',
});

const prepareTemplateVersionModelData = () => {
    // Initialize template for saving
    if (['PDF', 'EMAIL'].includes(templateModel.type)) {
        // Clear all cols html for email and PDF
        templateEditModel.template.body.forEach((row, rindex) => {
            row.cols.forEach((col, cindex) => {
                templateEditModel.template.body[rindex].cols[cindex].content.html = fixBrokenMarkup(
                    fixDOM(cleanHtml(col.content.html)),
                );
            });
        });

        templateVersionModel.source = renderTemplate(templateModel.type, templateEditModel, {
            langCode: templateVersionModel.langCode,
        });
    }
    templateVersionModel.sourceInline = templateVersionModel.source;
    templateVersionModel.uiConfig = JSON.stringify(templateEditModel);
};

const processPlacehoderDatasetsByAction = async (controlTemplateVersionId) => {
    // Get template placeholders
    const detectedPlaceholders = await harvestTemplate(templateVersionModel.source, []);
    // Get templateVersionPlaceholders
    const dbPlaceholderRefs = (await listPlaceholderRefs({
        isValid: true,
        templateVersionId: controlTemplateVersionId,
    })) || {
        items: [],
        totalCount: 0,
    };

    const requestConfig = preparePlaceholdersDatasets(
        allPlaceholders,
        detectedPlaceholders,
        dbPlaceholderRefs,
    );

    console.groupCollapsed('[TemplateVersionDetail] Placeholder reference sync');
    console.log('templateVersionId', controlTemplateVersionId);
    console.log('detectedPlaceholders', detectedPlaceholders);
    console.log('allPlaceholders', allPlaceholders.items);
    console.log('dbPlaceholderRefs', dbPlaceholderRefs.items);
    console.log('requestConfig', requestConfig);

    // Save new records
    for (const placeholderSave of requestConfig.save) {
        const payload = {
            templateVersionId: controlTemplateVersionId,
            placeholderDefinitionId: placeholderSave.placeholderDefinitionId,
            isRequired: true,
        };
        console.log('savePlaceholderRef payload', {
            placeholder: placeholderSave,
            payload,
        });
        const saveResult = await savePlaceholderRef('0', payload);
        console.log('savePlaceholderRef result', saveResult);
    }

    // Remove unused recors
    for (const placeholderRemove of requestConfig.remove) {
        console.log('removePlaceholderRef', placeholderRemove);
        const removeResult = await removePlaceholderRef(placeholderRemove.placeholderDefRefId);
        console.log('removePlaceholderRef result', removeResult);
    }
    console.groupEnd();
};

const saveCurrentVersion = async (closeTemplate = false) => {
    prepareTemplateVersionModelData();

    const res = await saveTemplateVersion(templateVersionId, templateVersionModel, saving);

    if (typeof res === 'object' && 'templateVersionId' in res) {
        // Delete unused placeholders
        // TO-DO
        let controlTemplateVersionId = templateVersionId;

        if (controlTemplateVersionId === '0') {
            controlTemplateVersionId = res.templateVersionId;
        }

        await processPlacehoderDatasetsByAction(controlTemplateVersionId);
        notify(
            {
                group: 'success',
                title: t('notifyOk'),
                text: `${t('notifyMessageSaved')}`,
            },
            2000,
        );

        if (closeTemplate === false && templateVersionId === '0') {
            window.location = `/template-version-edit/${templateId}/${controlTemplateVersionId}`;
        }

        if (closeTemplate === true) {
            router.push({
                name: 'template-versions',
                params: {
                    templateid: templateId,
                },
            });
        }
    } else {
        notify(
            {
                group: 'error',
                title: t('notifyError'),
                text: `Network or server error`,
            },
            7000,
        );
    }
};

const isValidForSave = async (closeTemplate = false) => {
    saving.active = true;
    templateValidation.version = checkVersionFormat(ark(templateVersionModel, 'version', ''));
    templateValidation.subject = ark(templateVersionModel, 'subject').length > 0;
    templateValidation.changeLog = ark(templateVersionModel, 'changeLog').length > 0;

    if (templateValidation.changeLog && templateValidation.subject && templateValidation.version) {
        await saveCurrentVersion(closeTemplate);
    } else {
        const messages = {
            version: t('templateVersion'),
            subject: t('templateVersionSubject'),
            changeLog: t('templateVersionChangeLog'),
            source: t('SourceCode'),
        };
        const display = [];
        Object.keys(templateValidation).map((item) => {
            const tplv = templateValidation[item];
            if (tplv === false) {
                display.push(messages[item]);
            }
        });
        notify(
            {
                group: 'error',
                title: t('notifyError'),
                text: `${t('MissingRequired', { subject: display.join(', ') })}`,
            },
            15000,
        );
        if (
            !templateValidation.changeLog ||
            !templateValidation.subject ||
            !templateValidation.version
        ) {
            setCurrentTab(2);
        }
    }
    saving.active = false;
};

const setCurrentTab = (activeIndex) => {
    activeComponents.sidebar.id = activeIndex;
};

provide('activeComponents', activeComponents);
provide('templateVersionModel', templateVersionModel);
provide('templateModel', templateModel);
provide('templateEditModel', templateEditModel);
provide('placeholders', allPlaceholders.items);

watch(activeComponents, () => {
    updateStateRepository();
});

const headMeta = navs().metaFind(route.name);
</script>
